import React, {Fragment, type FunctionComponent, useEffect} from "react"
import {BrandFilter} from "@App/Components/Filters"
import {ContactUs} from "@App/Components/ContactUs"
import {TriangleDivider} from "@App/Components/TriangleDivider"
import {useDispatch} from "react-redux";
import {actionCreators} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {Modal} from "@App/Components/Modal";
import type {Brand} from "@App/types";
import {Stepper} from "@App/Components/Stepper/Stepper";
import {useServices} from "@App/hooks/api/useServices";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {toInteger} from "lodash";
import {useHome} from "@App/hooks/useHome";
import {Loader} from "@App/Components/Loader";
import {ServiceStep} from "@App/Pages/Partner/Partials/ServiceStep";
import {SubServiceStep} from "@App/Pages/Partner/Partials/SubServiceStep";
import {EngineTypeStep} from "@App/Pages/Partner/Partials/EngineTypeStep";
import {LocationStep} from "@App/Pages/Partner/Partials/LocationStep";
import {Benefits} from "@App/Pages/Partner/Partials/Benefits";
import {
    ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored
} from "@App/utils/conditionals/combinations";
import {TireServiceStep} from "@App/Pages/Partner/Partials/TireServiceStep";
import {SecondaryTireServiceStep} from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import {ServiceIsGlass} from "@App/utils/conditionals/services";
import {HeroDL} from "@App/Components/HeroDL";

export type HomeProps = {
    embedded?: boolean
    onSubmit: () => void
}

export const Home: FunctionComponent<HomeProps> = ({embedded = false, onSubmit}: HomeProps) => {

    const dispatch = useDispatch();
    const location = useLocation()
    const {
        dismissModal,
    } = bindActionCreators(actionCreators, dispatch)

    const {t} = useTranslation()

    const {loading, error, services} = useServices()

    const {
        currentStep,
        setCurrentStep,
        selectBrand,
        selectEngine,
        togglePremiumService,
        togglePremiumServiceCarReplacementOrPickup,
        toggled,
        nextStep,
        organization,
        service,
        availableSubservices,
        availableSubservicesLoading,
        premiumServices,
        selectService,
        selectTireServiceTypeAndService,
        secondaryTireStepSelection,
        tireStepSelection,
        showSecondaryTireStep,
        place,
        bevSupport,
        brand,
        modalDismissed,
        stepToOneAndResetOthers
    } = useHome()

    const submit = () => {
        onSubmit?.()
    }

    useEffect(() => {
        if (toInteger(window.location.hash.substr(1)) > 0) {
            setCurrentStep(toInteger(window.location.hash.substr(1)))
        }
    }, [location])

    //if service is available initially (ie. on browserback) set step to 1
    useEffect(() => {
        if (service) {
            stepToOneAndResetOthers()
        }
    }, [])

    //if only one service is available set service to that and skip selection step (this needs deps, because services are empty on initial pageload)
    useEffect(() => {
        if (services && services.length === 1 && !service) {
            selectService(services[0], true)
            setCurrentStep(1)
        }
    }, [services])


    if (error) {
        console.log(error)
    }

    return (
        <Fragment>
            <HeroDL
                title={t("hero.title", {organization: organization.name})}
                subtitle={t("hero.subtitle", {organization: organization.name})}
                backgroundImage={organization.settings.hero.background_image_url ?? '/images/brand/arval-banner-darkened.jpg'}
                backgroundPosition={'bg-center'}
            />
            <Modal open={(organization.settings.welcome_modal.display ?? true) && !modalDismissed}
                   onClick={() => dismissModal(true)}
                   onClose={() => dismissModal(true)}
                   title={t('welcomeModal.title', {organization: organization.name})}
                   buttonText={'Weiter'} width="3xl">
                <div>
                    {t('welcomeModal.text', {organization: organization.name})}
                </div>
            </Modal>
            <div className="bg-white px-5 pt-5 container">
                <Stepper currentStep={currentStep} onChange={setCurrentStep}>
                    <Stepper.Step
                        title={t("Waehlen Sie einen Service.")}
                        subtitle={service && service.pivot.subtitle ? service.pivot.subtitle : null}
                        completedTitle={'Service: '}
                        completedGoBackLinkTitle={service ? (service.pivot.display_name ? service.pivot.display_name : service.name) : ''}>
                        <ServiceStep service={service} services={services} loading={loading} error={error} selectService={selectService} organization={organization}/>
                    </Stepper.Step>
                    {availableSubservicesLoading && (
                        <Loader></Loader>
                    )}
                    {(ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&  !availableSubservicesLoading) && (
                        <Stepper.Step
                            title={'Welchen Service benötigen Sie?'}
                            subtitle={null}
                            completedTitle={'Reifenservice' + ': '}
                            completedGoBackLinkTitle={tireStepSelection}>
                            <TireServiceStep services={services} selectService={selectTireServiceTypeAndService} organization={organization} secondary={false}/>
                        </Stepper.Step>
                    )}
                    {(showSecondaryTireStep && ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&  !availableSubservicesLoading) && (
                        <Stepper.Step
                            title={'Sind die Reifen eingelagert?'}
                            subtitle={null}
                            completedTitle={'Sind die Reifen eingelagert?' + ': '}
                            completedGoBackLinkTitle={secondaryTireStepSelection}>
                            <SecondaryTireServiceStep services={services} selectService={selectTireServiceTypeAndService} organization={organization} secondary={true}/>
                        </Stepper.Step>
                    )}
                    {(availableSubservices?.length > 0 || service == null) && !availableSubservicesLoading &&(
                        <Stepper.Step
                            title={t('chooseSubServices')}
                            subtitle={null}
                            completedTitle={'Services: '}
                            completedGoBackLinkTitle={`${premiumServices.length == 0 ? 'Keine Auswahl' : ''} ${premiumServices.map((selected) => (
                                `${selected.name}`
                            ))}`}>
                            <SubServiceStep availableSubservices={availableSubservices} premiumServices={premiumServices} toggled={toggled} togglePremiumServiceCarReplacementOrPickup={togglePremiumServiceCarReplacementOrPickup} togglePremiumService={togglePremiumService} nextStep={nextStep}/>
                        </Stepper.Step>
                    )}
                    {!ServiceIsGlass(service) && !availableSubservicesLoading && (
                        <Stepper.Step
                            title={t("Antriebsart")}
                            subtitle={null}
                            completedTitle={t("Antriebsart") + ': '}
                            completedGoBackLinkTitle={bevSupport ? t('Elektro-/Hybridmotor') : t('Verbrennungsmotor')}>
                            <EngineTypeStep selectEngine={selectEngine}/>
                        </Stepper.Step>
                    )}
                    <Stepper.Step
                        title={t('Waehlen Sie Ihre Marke.')}
                        completedTitle={'Marke: '}
                        completedGoBackLinkTitle={brand ? brand.name : ''}>
                        <Fragment>
                            <BrandFilter onSubmit={(brand: Brand) => selectBrand(brand)}/>
                        </Fragment>
                    </Stepper.Step>
                    <Stepper.Step title={t("Waehlen Sie Ihren Wunschort.")}
                                  completedTitle={''}
                                  completedGoBackLinkTitle={place ? place.name : ''}>
                        <LocationStep submit={submit} place={place} service={service}/>
                    </Stepper.Step>
                </Stepper>

            </div>
            {!embedded && (
                <div className="bg-neutral-100">
                    <TriangleDivider direction="left" background={"bg-white"}/>
                    <div className="px-5 py-5 container md:pb-20">
                        <div className="md:grid md:grid-cols-[50%_50%]">
                            <Benefits organization={organization}/>
                            <div className="pt-5 md:pt-0 md:ml-12">
                                <ContactUs/>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}
